import type { SignalCategoryType } from '@terra/shared-schemas-orchestrator';
import {
  AI_APPLICATION_SIGNAL_CATEGORIES,
  EXTERNAL_NETWORK_SIGNAL_UI_CATEGORIES,
  INTERNAL_NETWORK_SIGNAL_CATEGORIES as SHARED_INTERNAL_NETWORK_SIGNAL_CATEGORIES,
  MCP_SERVER_SCOPE_CATEGORIES,
  MCP_SERVER_SIGNAL_CATEGORIES,
  MCP_WEB_ALLOWED_CATEGORY_LIST,
  WEB_APP_SIGNAL_CATEGORIES,
} from '@terra/shared-schemas-orchestrator';

export type SignalCategoryTargetType =
  | 'EXTERNAL_NETWORK'
  | 'INTERNAL_NETWORK'
  | 'WEB_APPLICATION'
  | 'AGENTIC_AI'
  | 'AI_APPLICATION'
  | 'MCP_SERVER';

export const INTERNAL_NETWORK_SIGNAL_CATEGORIES: readonly SignalCategoryType[] =
  [...SHARED_INTERNAL_NETWORK_SIGNAL_CATEGORIES];

export const EXTERNAL_NETWORK_SIGNAL_CATEGORIES: readonly SignalCategoryType[] =
  [...EXTERNAL_NETWORK_SIGNAL_UI_CATEGORIES];

export const MCP_SERVER_HTTP_TRANSPORT_SIGNAL_CATEGORIES: readonly SignalCategoryType[] =
  [...MCP_WEB_ALLOWED_CATEGORY_LIST];

export function getMcpServerScopeCategorySections(): {
  mcpNativeCategories: SignalCategoryType[];
  httpTransportCategories: SignalCategoryType[];
} {
  return {
    mcpNativeCategories: [...MCP_SERVER_SIGNAL_CATEGORIES],
    httpTransportCategories: [...MCP_WEB_ALLOWED_CATEGORY_LIST],
  };
}

export function getSignalCategoriesForTarget({
  targetType,
}: {
  targetType: SignalCategoryTargetType;
}): SignalCategoryType[] {
  switch (targetType) {
    case 'INTERNAL_NETWORK':
      return [...INTERNAL_NETWORK_SIGNAL_CATEGORIES];
    case 'EXTERNAL_NETWORK':
      return [...EXTERNAL_NETWORK_SIGNAL_CATEGORIES];
    case 'WEB_APPLICATION':
      return [...WEB_APP_SIGNAL_CATEGORIES];
    case 'AGENTIC_AI':
    case 'AI_APPLICATION':
      return [...AI_APPLICATION_SIGNAL_CATEGORIES];
    case 'MCP_SERVER':
      return [...MCP_SERVER_SCOPE_CATEGORIES];
    default:
      return [];
  }
}

export function normalizeMcpServerSignalCategoriesInScope({
  stored,
}: {
  stored: SignalCategoryType[];
}): SignalCategoryType[] {
  const mcpScope = getSignalCategoriesForTarget({ targetType: 'MCP_SERVER' });
  const mcpScopeSet = new Set<SignalCategoryType>(mcpScope);

  if (stored.length === 0) {
    return mcpScope;
  }

  const normalized = stored.filter((category) => mcpScopeSet.has(category));

  return normalized.length > 0 ? normalized : mcpScope;
}

export function orderMcpServerScopeCategories(
  categories: SignalCategoryType[],
): SignalCategoryType[] {
  const { mcpNativeCategories, httpTransportCategories } =
    getMcpServerScopeCategorySections();
  const categorySet = new Set(categories);
  const orderedNative = mcpNativeCategories.filter((category) =>
    categorySet.has(category),
  );
  const orderedHttpTransport = httpTransportCategories.filter((category) =>
    categorySet.has(category),
  );
  const orderedCategorySet = new Set([
    ...orderedNative,
    ...orderedHttpTransport,
  ]);
  const remaining = categories.filter(
    (category) => !orderedCategorySet.has(category),
  );

  return [...orderedNative, ...orderedHttpTransport, ...remaining];
}

export function resolveStoredSignalCategoriesInScope({
  stored,
  targetType,
}: {
  stored: SignalCategoryType[];
  targetType: SignalCategoryTargetType;
}): SignalCategoryType[] {
  if (stored.length > 0) {
    return stored;
  }

  return getSignalCategoriesForTarget({ targetType });
}

const categoriesEqual = (
  left: SignalCategoryType[],
  right: SignalCategoryType[],
) =>
  left.length === right.length &&
  left.every((category) => right.includes(category));

export function areSignalCategoriesInScopeDirty({
  draft,
  stored,
  targetType,
}: {
  draft: SignalCategoryType[];
  stored: SignalCategoryType[];
  targetType: SignalCategoryTargetType;
}): boolean {
  return !categoriesEqual(
    draft,
    resolveStoredSignalCategoriesInScope({ stored, targetType }),
  );
}
